import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colors as colors99dUi } from '@99designs/emu/settings';
import getHighContrastTextColor from '../../../lib/getHighContrastTextColor';
import { colors, spaces } from '../../../lib/styles';
import { getElementHeight } from '../helpers';
import { ControlsIcon } from './ControlsIcon';

const Trigger = styled.button<{ isOpen: boolean; featureColor: string }>`
    position: relative;
    width: 100%;
    border: none;
    border-radius: 0;
    padding-top: ${spaces.space9}px;
    padding-bottom: ${spaces.space9}px;
    padding-left: 0;
    padding-right: ${spaces.space10}px;
    background: transparent;
    text-align: left;
    color: ${({ featureColor }) =>
        featureColor ? getHighContrastTextColor(featureColor) : colors.grey800};

    &:hover,
    &:focus {
        box-shadow: none;
        text-decoration: none;
        outline: none;
    }

    &:focus {
        &:first-child {
            ${ControlsIcon} {
                outline: thin dotted ${colors99dUi.withAlpha(colors.grey800, 0.7)};
                outline-offset: 5px;
            }
        }
    }
`;

const Content = styled.div<{
    ref: React.RefObject<HTMLDivElement>;
    isOpen: boolean;
    bodyHeight: number;
}>(({ isOpen, bodyHeight }) => {
    return css`
        height: 0;
        min-height: 0;
        visibility: hidden;
        opacity: 0;
        overflow: hidden;
        transition: all 0.3s, visibility 0.3s;

        ${isOpen &&
        css`
            height: auto;
            min-height: ${bodyHeight}px;
            visibility: visible;
            opacity: 1;
        `}
    `;
});

export interface AccordionItemValue {
    id: string;
    title: string;
}

interface Props {
    id: string;
    title: string;
    isOpen?: boolean;
    featureColor?: string;
    children: React.ReactNode;
    triggerContent: React.ReactNode;
    onOpen?: (accordionItemValue: AccordionItemValue) => void;
    onClick?: () => void;
}

const BaseAccordionItem = ({
    id,
    title,
    isOpen = false,
    featureColor = '',
    children,
    triggerContent,
    onOpen,
    onClick,
}: Props) => {
    const triggerId = `accordion-trigger-${id}`;
    const contentId = `accordion-panel-${id}`;
    const [bodyHeight, setBodyHeight] = useState(0);
    const itemContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = itemContentRef.current;
        if (element) {
            setBodyHeight(getElementHeight(element));

            let timeoutId: ReturnType<typeof setTimeout>;

            const resizeListener = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    element && setBodyHeight(getElementHeight(element));
                }, 300);
            };

            window.addEventListener('resize', resizeListener);

            return () => {
                window.removeEventListener('resize', resizeListener);
            };
        }
        return;
    }, [itemContentRef]);

    return (
        <>
            <Trigger
                id={triggerId}
                onClick={() => {
                    onClick && onClick();
                    if (!isOpen && onOpen) {
                        onOpen({ id, title });
                    }
                }}
                isOpen={isOpen}
                featureColor={featureColor}
                aria-controls={contentId}
                aria-expanded={isOpen}
            >
                {triggerContent}
            </Trigger>
            <Content
                id={contentId}
                ref={itemContentRef}
                isOpen={isOpen}
                bodyHeight={bodyHeight}
                aria-hidden={!isOpen}
                aria-labelledby={triggerId}
            >
                {children}
            </Content>
        </>
    );
};

export default BaseAccordionItem;
